import React, { useState, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { supabase } from "./supabase";
import { DraftTicker } from "./DraftTicker";
import { PickAlert } from "./PickAlert";
import { DraftBoard } from "./DraftBoard";
import { LiveRosters } from "./LiveRosters";
import { DraftOrder } from "./DraftOrder";
import { NowPicking } from "./NowPicking";
import { PlayerPool } from "./PlayerPool";
import { AtmosphericCountdown } from "./AtmosphericCountdown";
import { CinematicOverlay } from "./CinematicOverlay";

interface PublicTrackerProps {
  initialSessionId?: string;
}

export const PublicTracker: React.FC<PublicTrackerProps> = ({
  initialSessionId,
}) => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [animationType, setAnimationType] = useState<"start" | "end" | null>(
    null
  );

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.overflowX = "hidden";
    document.body.style.backgroundColor = "#151513";
    return () => {
      document.body.style.margin = "";
      document.body.style.padding = "";
    };
  }, []);

  const fetchSessions = async () => {
    const { data } = await supabase
      .from("draft_sessions")
      .select("*")
      .order("year", { ascending: false });

    if (data && data.length > 0) {
      setSessions(data);
      if (!selectedSession) {
        const target = initialSessionId
          ? data.find((s: any) => s.id === initialSessionId)
          : data.find((s: any) => s.is_current) || data[0];
        setSelectedSession(target);
      } else {
        const updated = data.find((s: any) => s.id === selectedSession.id);
        if (updated) setSelectedSession(updated);
      }
    }
  };

  useEffect(() => {
    fetchSessions();

    const sessionChannel = supabase
      .channel("public-sync")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "draft_sessions" },
        () => fetchSessions()
      )
      .subscribe();

    // UNDO REFRESH: Refreshes the full page when a pick is deleted
    const undoChannel = supabase
      .channel("undo-refresh")
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "picks" },
        () => {
          window.location.reload();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sessionChannel);
      supabase.removeChannel(undoChannel);
    };
  }, [selectedSession?.id, initialSessionId]);

  if (!selectedSession) return null;

  if (selectedSession.status === "pre-draft") {
    return <AtmosphericCountdown />;
  }

  return (
    <div
      className="main-container"
      style={{
        backgroundColor: "#151513",
        minHeight: "100vh",
        fontFamily: "'Oswald', sans-serif",
        position: "relative",
      }}
    >
      <style>
        {`
          @media (max-width: 768px) {
            header { padding-top: 10px !important; }
            .header-content { 
              flex-direction: column !important; 
              gap: 5px !important; 
              padding: 15px 10px !important; 
            }
            .header-title { font-size: 1.1rem !important; }
            .session-selector-container { 
              position: static !important; 
              justify-content: center !important; 
              width: 100%;
              order: 3; /* Moves it under the title in the flex column */
              margin-top: 5px;
            }
            main { padding: 15px !important; }
            .now-picking-container { min-height: 80px !important; padding: 10px; }
          }
        `}
      </style>

      <AnimatePresence mode="wait">
        {animationType === "start" && (
          <CinematicOverlay
            key="start"
            title="EMPIRE LEAGUE DRAFT"
            subtitle="NOW IN SESSION"
            onComplete={() => setAnimationType(null)}
          />
        )}
        {animationType === "end" && (
          <CinematicOverlay
            key="end"
            title="DRAFT CONCLUDED"
            subtitle="THANK YOU FOR WATCHING!"
            onComplete={() => setAnimationType(null)}
          />
        )}
      </AnimatePresence>

      <PickAlert />

      <header
        style={{
          borderBottom: "3px solid #22565a",
          textAlign: "center",
          backgroundColor: "#080808",
          position: "relative",
          zIndex: 10,
        }}
      >
        <div
          className="header-content"
          style={{
            padding: "15px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "15px",
          }}
        >
          <img
            src="https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/Empire%20League%20Logo.png"
            alt="Empire League Logo"
            style={{ height: "40px", width: "auto" }}
          />
          <h1
            className="header-title"
            style={{
              color: "#f9eeda",
              margin: 0,
              fontSize: "1.4rem",
              textTransform: "uppercase",
              letterSpacing: "2px",
              fontWeight: "900",
            }}
          >
            EMPIRE DRAFT CENTER
          </h1>

          <div
            className="session-selector-container"
            style={{
              position: "absolute",
              top: "12px",
              right: "15px",
              zIndex: 20,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                background: "rgba(34, 86, 90, 0.15)",
                border: "1px solid #22565a",
                borderRadius: "4px",
                padding: "2px 10px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  color: "#22565a",
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  letterSpacing: "1px",
                }}
              >
                SEASON:
              </span>
              <select
                value={selectedSession.id || ""}
                onChange={(e) => {
                  const session = sessions.find((s) => s.id === e.target.value);
                  if (session) setSelectedSession(session);
                }}
                style={{
                  background: "transparent",
                  color: "#f9eeda",
                  border: "none",
                  padding: "2px",
                  borderRadius: "4px",
                  fontFamily: "'Oswald', sans-serif",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  outline: "none",
                  textTransform: "uppercase",
                }}
              >
                {sessions.map((s: any) => (
                  <option
                    key={s.id}
                    value={s.id}
                    style={{ background: "#111" }}
                  >
                    {s.year}{" "}
                    {s.status === "live" ? "• LIVE" : s.status.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <DraftTicker sessionId={selectedSession.id} />
          </div>
        </div>

        {selectedSession.status === "live" && (
          <div
            className="now-picking-container"
            style={{
              width: "100%",
              minHeight: "120px",
              background: "linear-gradient(180deg, #080808 0%, #0d0d0d 100%)",
              borderTop: "1px solid #22565a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NowPicking sessionId={selectedSession.id} />
          </div>
        )}
      </header>

      <main style={{ padding: "30px", paddingBottom: "120px" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          <DraftBoard sessionId={selectedSession.id} />
          <DraftOrder sessionId={selectedSession.id} />
          <PlayerPool
            isAdmin={false}
            sessionId={selectedSession.id}
            canDraft={false}
          />
          <LiveRosters isAdmin={false} sessionId={selectedSession.id} />
        </div>
      </main>
    </div>
  );
};
